'use client'

import {useEffect, useMemo, useState} from 'react'
import {usePathname, useSearchParams} from 'next/navigation'
import {useTranslations} from 'next-intl'
import {AlertTriangle, ArrowRight, Building2, Loader2} from 'lucide-react'
import {Button} from '@fitro/customer-ui/Button'
import {LinkButton} from '../ui/LinkButton'
import {useAuth} from '@fitro/customer-host/auth'
import {getPathWithSearch} from '../../lib/returnTo'
import {resolveCustomerRecoveryPath} from '../../lib/customer-recovery-path'
import {buildTenantAbsoluteUrl, resolvePartnerHostInfo} from '../../lib/partner-host'
import {
    listCustomerPartnerRecoveryOptions,
    type CustomerPartnerRecoveryOption
} from '../../services/partner-recovery.service'

type PartnerRecoveryNoticeProps = {
    status: 'unknown' | 'unconfigured'
    slug?: string
}

function hasActiveCustomerLink(customerStatus?: string | null) {
    if (!customerStatus) return true
    const normalized = customerStatus.trim().toUpperCase()
    return normalized === 'ACTIVE'
}

export function PartnerRecoveryNotice({status, slug}: PartnerRecoveryNoticeProps) {
    const t = useTranslations('landing.publicEntry.partnerRecovery')
    const {isLoaded, isSignedIn} = useAuth()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const [partners, setPartners] = useState<CustomerPartnerRecoveryOption[]>([])
    const [isLoadingPartners, setIsLoadingPartners] = useState(false)
    const [partnerLoadFailed, setPartnerLoadFailed] = useState(false)
    const [redirectFailed, setRedirectFailed] = useState(false)
    const [redirectingSlug, setRedirectingSlug] = useState<string | null>(null)
    const [targetDomain, setTargetDomain] = useState<string | null>(null)

    const currentPath = useMemo(() => {
        return resolveCustomerRecoveryPath(getPathWithSearch(pathname ?? '/calendar', searchParams))
    }, [pathname, searchParams])

    const signInHref = useMemo(() => {
        return `/sign-in?redirect_url=${encodeURIComponent(currentPath)}`
    }, [currentPath])

    const description = useMemo(() => {
        if (status === 'unconfigured') return t('description.unconfigured')
        if (slug) return t('description.unknownWithSlug', {slug})
        return t('description.unknown')
    }, [status, slug, t])

    useEffect(() => {
        if (typeof window === 'undefined') return

        const hostInfo = resolvePartnerHostInfo(window.location.host)
        if (hostInfo.zone === 'fitro') {
            setTargetDomain('fitro.pl')
            return
        }
        if (hostInfo.zone === 'fitrodev') {
            setTargetDomain('fitrodev.pl')
            return
        }
        if (hostInfo.zone === 'localhost') {
            setTargetDomain('localhost')
            return
        }

        setTargetDomain(null)
    }, [])

    useEffect(() => {
        if (!isLoaded || !isSignedIn) {
            setPartners([])
            setIsLoadingPartners(false)
            setPartnerLoadFailed(false)
            setRedirectFailed(false)
            return
        }

        let cancelled = false
        setIsLoadingPartners(true)
        setPartnerLoadFailed(false)
        setRedirectFailed(false)

        listCustomerPartnerRecoveryOptions()
            .then((response) => {
                if (cancelled) return
                setPartners(response)
            })
            .catch(() => {
                if (cancelled) return
                setPartners([])
                setPartnerLoadFailed(true)
            })
            .finally(() => {
                if (cancelled) return
                setIsLoadingPartners(false)
            })

        return () => {
            cancelled = true
        }
    }, [isLoaded, isSignedIn])

    const handleSelectPartner = (partnerSlug: string) => {
        const targetUrl = buildTenantAbsoluteUrl(partnerSlug, {path: currentPath})
        if (!targetUrl) {
            setRedirectFailed(true)
            return
        }

        setRedirectFailed(false)
        setRedirectingSlug(partnerSlug)
        window.location.assign(targetUrl)
    }

    return (
        <main className="min-h-svh w-full bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-6">
            <div className="w-full max-w-3xl rounded-3xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-sm">
                <div className="flex items-start gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-warning-50 text-warning-600">
                        <AlertTriangle className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-xs uppercase tracking-[0.25em] text-slate-400">
                            {t('eyebrow')}
                        </p>
                        <h1 className="mt-2 text-2xl font-semibold text-slate-900 dark:text-gray-100">
                            {t('title')}
                        </h1>
                        <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-gray-400">
                            {description}
                        </p>
                    </div>
                </div>

                {slug ? (
                    <div className="mt-5 rounded-lg bg-slate-100 dark:bg-gray-800 px-3 py-2 text-xs text-slate-600 dark:text-gray-400">
                        <span className="font-semibold text-slate-700 dark:text-gray-300">{t('enteredAddress')}</span> {slug}
                    </div>
                ) : null}

                {!isLoaded ? (
                    <div className="mt-8 flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 px-4 py-4 text-sm text-slate-600 dark:text-gray-400">
                        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                        {t('loadingSession')}
                    </div>
                ) : !isSignedIn ? (
                    <div className="mt-8 rounded-2xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 p-5">
                        <p className="text-sm text-slate-700 dark:text-gray-300">
                            {t('signedOut.body')}
                        </p>
                        <div className="mt-4">
                            <LinkButton href={signInHref} variant="primary" size="medium">
                                {t('signedOut.cta')}
                            </LinkButton>
                        </div>
                    </div>
                ) : isLoadingPartners ? (
                    <div className="mt-8 flex items-center gap-3 rounded-2xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 px-4 py-4 text-sm text-slate-600 dark:text-gray-400">
                        <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-[spin_1.5s_linear_infinite]" />
                        {t('loadingStudios')}
                    </div>
                ) : partnerLoadFailed ? (
                    <div className="mt-8 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-700">
                        {t('loadError')}
                    </div>
                ) : partners.length === 0 ? (
                    <div className="mt-8 rounded-2xl border border-slate-200 dark:border-gray-700 bg-slate-50 dark:bg-gray-800 p-5 text-sm text-slate-700 dark:text-gray-300">
                        {t('empty')}
                    </div>
                ) : (
                    <div className="mt-8 space-y-3">
                        {partners.map((partner) => {
                            const isRedirecting = redirectingSlug === partner.slug
                            const canEnterPartner = partner.active && hasActiveCustomerLink(partner.customerStatus)

                            return (
                                <div
                                    key={partner.slug}
                                    className="flex flex-col gap-4 rounded-2xl border border-slate-200 dark:border-gray-700 p-5 sm:flex-row sm:items-center sm:justify-between"
                                >
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 dark:bg-gray-800 text-slate-500 dark:text-gray-400">
                                                <Building2 className="h-5 w-5" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="truncate text-base font-medium text-slate-900 dark:text-gray-100">
                                                    {partner.displayName || partner.slug}
                                                </p>
                                                {targetDomain ? (
                                                    <p className="truncate text-sm text-slate-500 dark:text-gray-400">
                                                        {partner.slug}.{targetDomain}
                                                    </p>
                                                ) : null}
                                            </div>
                                        </div>
                                        {!canEnterPartner ? (
                                            <p className="mt-3 text-sm text-warning-700">
                                                {partner.active
                                                    ? t('inactive.linked')
                                                    : t('inactive.studio')}
                                            </p>
                                        ) : null}
                                    </div>
                                    <Button
                                        type="button"
                                        variant={canEnterPartner ? 'primary' : 'outline'}
                                        size="medium"
                                        disabled={!canEnterPartner || Boolean(redirectingSlug)}
                                        loading={isRedirecting}
                                        rightIcon={!isRedirecting ? <ArrowRight className="h-4 w-4" /> : undefined}
                                        onClick={() => handleSelectPartner(partner.slug)}
                                    >
                                        {t('enterStudio')}
                                    </Button>
                                </div>
                            )
                        })}
                    </div>
                )}

                {redirectFailed ? (
                    <div className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
                        {t('redirectError')}
                    </div>
                ) : null}
            </div>
        </main>
    )
}
